if (!customElements.get('skin-assessment')) {
  customElements.define(
    'skin-assessment',
    class SkinAssessment extends HTMLElement {
      constructor() {
        super();
        this.slides = this.querySelectorAll('.skin-assessment__slide');
        this.continueButtons = this.querySelectorAll('[data-action="next"]');
        this.retakeButton = this.querySelector('[data-action="retake"]');
        this.contactForm = this.querySelector('#skin-assessment-contact-form');
        this.form = this.querySelector('#skin-assessment-form');
        this.locationModal = this.querySelector('#location-modal');
        this.locationAllowBtn = this.querySelector('[data-action="allow-location"]');
        this.locationSkipBtn = this.querySelector('[data-action="skip-location"]');
        this.consultationModal = this.querySelector('#consultation-modal');
        this.consultationConfirmBtn = this.querySelector('[data-action="confirm-consultation"]');
        this.consultationCancelBtn = this.querySelector('[data-action="cancel-consultation"]');
        this.backButtons = this.querySelectorAll('[data-action="back"]');
        
        // Store current booking product data
        this.currentBookingProduct = null;
        
        // Permission state: 'prompt', 'granted', or 'denied'
        // Will be set during init() after checking permission state
        this.locationPermissionState = null;
        
        // Track if component has been initialized (to prevent scroll on initial load)
        this.isInitialized = false;
        
        // Track if we're resolving the initial step (to show loader)
        this.isResolvingInitialStep = true;

        // When true, Back on slide 5 goes to slide 8 (report list) instead of slide 4
        this.cameFromReportList = false;
        // Timestamp of the report currently viewed (from report list item click)
        this.currentReportTimestamp = '';

        // Initialize single source of truth state object
        this.initializeState();

        this.init();
      }

      // Initialize single source of truth state object
      initializeState() {
        // Create or get global state object
        if (!window.skinAssessmentState) {
          window.skinAssessmentState = {
            customerId: null,
            formData: {},
            captureBase64: null,
            getTagsResponse: null,
            location: {
              allowed: false,
              latitude: null,
              longitude: null,
              skipped: false,
              error: null
            }
            // Note: currentSlide is not persisted - slide selection derived from data only
          };
        }

      }

      // Load state from localStorage (ONE-TIME LOAD on initialization)
      loadAssessmentState() {
        try {
          // Load main assessment data
          const savedData = localStorage.getItem('skinAssessmentDatas');
          if (savedData) {
            const data = JSON.parse(savedData);
            
            // Hydrate state from localStorage
            window.skinAssessmentState.customerId = data.customerId || localStorage.getItem('skinAssessmentCustomerId') || null;
            window.skinAssessmentState.formData = data.formData || data || {};
            window.skinAssessmentState.captureBase64 = data.captureBase64 || null;
            window.skinAssessmentState.getTagsResponse = data.getTagsResponse || null;
            
            // Location data
            if (data.latitude && data.longitude) {
              window.skinAssessmentState.location = {
                allowed: data.locationAllowed || false,
                latitude: data.latitude,
                longitude: data.longitude,
                skipped: data.locationSkipped || false,
                error: data.locationError || null
              };
            }
          }
          
          // Note: currentSlide is no longer persisted
          // Slide selection is derived from data only
        } catch (e) {
          console.warn('Could not load assessment state from localStorage:', e);
        }
      }

      // Get current state (read-only access)
      getAssessmentState() {
        return { ...window.skinAssessmentState };
      }

      // Update state (partial update)
      updateAssessmentState(partialData) {
        window.skinAssessmentState = {
          ...window.skinAssessmentState,
          ...partialData
        };
        // Auto-persist after update
        this.persistAssessmentState();
      }

      // Persist state to localStorage (single persistence function)
      persistAssessmentState() {
        try {
          const state = window.skinAssessmentState;
          
          // Persist main assessment data
          const dataToStore = {
            customerId: state.customerId,
            formData: state.formData,
            captureBase64: state.captureBase64,
            getTagsResponse: state.getTagsResponse,
            locationAllowed: state.location.allowed,
            latitude: state.location.latitude,
            longitude: state.location.longitude,
            locationSkipped: state.location.skipped,
            locationError: state.location.error
          };
          
          localStorage.setItem('skinAssessmentData', JSON.stringify(dataToStore));
          
          // Persist customer ID separately (for backward compatibility)
          if (state.customerId) {
            localStorage.setItem('skinAssessmentCustomerId', state.customerId);
          }
          
          // Note: currentSlide is no longer persisted
          // Slide selection is derived from data only
        } catch (e) {
          console.warn('Could not persist assessment state to localStorage:', e);
        }
      }

      // Clear all state
      clearAssessmentState() {
        window.skinAssessmentState = {
          customerId: null,
          formData: {},
          captureBase64: null,
          getTagsResponse: null,
          location: {
            allowed: false,
            latitude: null,
            longitude: null,
            skipped: false,
            error: null
          }
          // Note: currentSlide is not persisted - slide selection derived from data only
        };
        
        try {
          localStorage.removeItem('skinAssessmentData');
          localStorage.removeItem('skinAssessmentCustomerId');
          // Note: currentSlide is no longer persisted
        } catch (e) {
          console.warn('Could not clear localStorage:', e);
        }
      }

      init() {
        console.log(window.skinAssessmentData,"skinAssessmentData");
        // Get loader element reference
        this.initialLoader = this.querySelector('#skin-assessment-initial-loader');
        
        // INITIAL LOAD: Hide all slides and show loader
        if (this.isResolvingInitialStep) {
          // Hide all slides initially
          this.slides.forEach((slide) => {
            slide.classList.add('skin-assessment__slide--hidden');
            slide.setAttribute('aria-hidden', 'true');
          });
          
          // Show loader
          if (this.initialLoader) {
            this.initialLoader.classList.remove('skin-assessment__initial-loader--hidden');
          }
        }
        
        // ONE-TIME LOAD: Load state from localStorage
        this.loadAssessmentState();
        
        // Sync component properties from state
        // Note: currentSlide is derived from data, not persisted
        this.currentSlide = 1; // Will be set by showSlide()
        this.formData = window.skinAssessmentState.formData;
        
        // Sync window globals from state (for backward compatibility with liquid file)
        if (window.skinAssessmentState.captureBase64) {
          window.skinAssessmentCaptureBase64 = window.skinAssessmentState.captureBase64;
        }
        if (window.skinAssessmentState.getTagsResponse) {
          window.skinAssessmentGetTagsResponse = window.skinAssessmentState.getTagsResponse;
        }
        if (window.skinAssessmentState.location.latitude && window.skinAssessmentState.location.longitude) {
          window.skinAssessmentLatitude = window.skinAssessmentState.location.latitude;
          window.skinAssessmentLongitude = window.skinAssessmentState.location.longitude;
        }
        
        // STEP 1: DATA COLLECTION - Check geolocation permission state
        // This is non-blocking and won't trigger permission prompt
        // checkLocationPermissionState never throws, so we can safely await it
        // NOTE: Location permission check happens here for initialization,
        // but location modal/flow only triggers AFTER customer is created and forms are complete
        this.checkLocationPermissionState().then((permissionState) => {
          // Store permission state (data collection result)
          this.locationPermissionState = permissionState;
        }).catch((error) => {
          // ERROR HANDLING ONLY - Log error, set safe default
          // This should never happen since checkLocationPermissionState never throws
          // But we handle it defensively
          console.error('Unexpected error checking permission state:', error);
          this.locationPermissionState = 'prompt';
        });
        
        // If URL has customerId, show loader and go to report list slide after fetch (no recovery flow)
        const urlCustomerId = new URLSearchParams(window.location.search).get('customerId');
        if (urlCustomerId && typeof urlCustomerId === 'string') {
          this.handleReportListFromUrl();
          this.initReportBackButton();
          this.backButtons.forEach((button) => {
            button.addEventListener('click', this.handleBack.bind(this));
          });
          this.bottomNavItems = this.querySelectorAll('.bottom-nav-item[data-nav]');
          this.bottomNavItems.forEach((item) => {
            item.addEventListener('click', this.handleBottomNavClick.bind(this));
          });
          this.initializeFormFields();
          this.loadFormDataIntoForms();
          this.isInitialized = true;
        }else{
           // Back buttons
          this.backButtons.forEach((button) => {
            button.addEventListener('click', this.handleBack.bind(this));
          });
          // RECOVERY RULES: Determine correct slide and handle customer creation if needed
          this.handleRecoveryFlow();
        }

        // Attach event listeners
        this.continueButtons.forEach((button) => {
          button.addEventListener('click', this.handleContinue.bind(this));
        });
 
        if (this.retakeButton) {
          this.retakeButton.addEventListener('click', this.handleRetake.bind(this));
        }
 
        // Location modal buttons
        if (this.locationAllowBtn) {
          this.locationAllowBtn.addEventListener('click', this.handleLocationAllow.bind(this));
        }
 
        if (this.locationSkipBtn) {
          this.locationSkipBtn.addEventListener('click', this.handleLocationSkip.bind(this));
        }

        // Consultation modal buttons
        if (this.consultationConfirmBtn) {
          this.consultationConfirmBtn.addEventListener('click', this.handleConsultationConfirm.bind(this));
        }
        if (this.consultationCancelBtn) {
          this.consultationCancelBtn.addEventListener('click', this.handleConsultationCancel.bind(this));
        }

        // Book Now buttons on Slide 7
        const bookNowButtons = this.querySelectorAll('.book-appointment-js');
        bookNowButtons.forEach((button) => {
          button.addEventListener('click', this.handleBookNowClick.bind(this));
        });

        // Bottom navigation buttons
        this.bottomNavItems = this.querySelectorAll('.bottom-nav-item[data-nav]');
        this.bottomNavItems.forEach((item) => {
          item.addEventListener('click', this.handleBottomNavClick.bind(this));

        });

        // Programmatic slide change (e.g. from widget after capture)
        this.addEventListener('skin-assessment-goto-slide', (e) => {
          console.log(e.detail,"skin-assessment-goto-slide",e.detail.slide);
          if (e.detail && typeof e.detail.slide === 'number') {
            this.showSlide(e.detail.slide);
          }
        });
 
        // Initialize form fields and attach event listeners
        this.initializeFormFields();
 
        // Load saved form data
        this.loadFormDataIntoForms();
        
        // Mark component as initialized after all setup is complete
        // This prevents scroll on initial load
        this.isInitialized = true;

        document.addEventListener('click', function (e) {
          const btn = e.target.closest('.quick-add__submit');
          if (!btn) return;
          const form = btn.closest('form');
          if (!form) return;
          e.preventDefault();
          e.stopPropagation();
          if (typeof form.requestSubmit === 'function') {
            form.requestSubmit(btn);
          } else {
            form.submit();
          }
        }, true);

        // Intercept product form submissions from skin-assessment to add 'Dermat Consultation' property
        this.interceptProductFormSubmissions();
      }

      // Intercept product form submissions within skin-assessment component
      interceptProductFormSubmissions() {
        const skinAssessmentElement = this;
        
        // Listen for form submissions on product forms within this skin-assessment component
        this.addEventListener('submit', function(e) {
          const form = e.target;
          
          // Only intercept forms that are product add-to-cart forms
          if (form.getAttribute('data-type') !== 'add-to-cart-form') {
            return;
          }

          // Check if form is within product cards in skin-assessment (slide 6)
          const slide6 = skinAssessmentElement.querySelector('.skin-assessment__slide-6');
          if (!slide6 || !slide6.contains(form)) {
            return;
          }

          // Only add 'Dermat Consultation' property if the product has the derma_consultant tag
          // Find the product card wrapper (li.grid__item) that contains this form
          const productCard = form.closest('li.grid__item, .grid__item');
          const isDermaConsultant = productCard && productCard.hasAttribute('data-derma-consultant') && 
                                    productCard.getAttribute('data-derma-consultant') === 'true';

          // Only add property if it's a derma_consultant product
          if (isDermaConsultant) {
            // Add the 'Dermat Consultation' property to the form
            // Create a hidden input for the property if it doesn't exist
            const existingProperty = form.querySelector('input[name="properties[Dermat Consultation]"]');
            if (!existingProperty) {
              const propertyInput = document.createElement('input');
              propertyInput.type = 'hidden';
              propertyInput.name = 'properties[Dermat Consultation]';
              propertyInput.value = 'true';
              form.appendChild(propertyInput);
            }
          }
        }, true);
      }

      initializeFormFields() {
        // Initialize contact form
        if (this.contactForm) {
          this.contactForm.addEventListener('input', this.saveContactFormData.bind(this));
          this.contactForm.addEventListener('change', this.saveContactFormData.bind(this));
          // Also attach to individual inputs for better compatibility
          const contactInputs = this.contactForm.querySelectorAll('input, textarea, select');
          contactInputs.forEach((input) => {
            input.addEventListener('input', this.saveContactFormData.bind(this));
            input.addEventListener('change', this.saveContactFormData.bind(this));
            // Ensure inputs are not disabled and are interactive
            if (input.disabled) {
              input.disabled = false;
            }
            input.style.pointerEvents = 'auto';
            input.removeAttribute('readonly');
          });
          
          // Add WhatsApp number validation
          const whatsappInput = this.contactForm.querySelector('#whatsapp-number');
          if (whatsappInput) {
            this.addWhatsAppValidation(whatsappInput);
          }
          
          // Add email validation
          const emailInput = this.contactForm.querySelector('#email-address');
          if (emailInput) {
            this.addEmailValidation(emailInput);
          }
        }
 
        // Initialize main form
        if (this.form) {
          this.form.addEventListener('input', this.saveFormData.bind(this));
          this.form.addEventListener('change', this.saveFormData.bind(this));
          
          // Also attach to individual inputs for better compatibility
          const formInputs = this.form.querySelectorAll('input, textarea, select');
          formInputs.forEach((input) => {
            input.addEventListener('input', this.saveFormData.bind(this));
            input.addEventListener('change', this.saveFormData.bind(this));
            // Ensure inputs are not disabled and are interactive
            if (input.disabled) {
              input.disabled = false;
            }
            input.style.pointerEvents = 'auto';
            input.removeAttribute('readonly');
            
            // For radio buttons, ensure they work properly and show visual state
            if (input.type === 'radio') {
              // Remove any inline styles that might interfere
              input.style.display = '';
              input.style.visibility = '';
              input.style.opacity = '';
              
              // Find the label associated with this radio button
              const label = input.closest('label.skin-assessment__radio-label');
              if (label) {
                // Ensure label has pointer events enabled
                label.style.pointerEvents = 'auto';
                
                // Add click handler to label to ensure radio is checked when label is clicked
                label.addEventListener('click', function(e) {
                  // If the click is directly on the radio input itself, let browser handle it naturally
                  if (e.target === input) {
                    return;
                  }
                  
                  // Check the associated radio button
                  // The browser should handle this via the 'for' attribute, but we ensure it works
                  if (!input.checked) {
                    input.checked = true;
                    
                    // Uncheck other radios in the same group
                    const name = input.name;
                    const form = input.closest('form');
                    if (form) {
                      const radios = form.querySelectorAll(`input[name="${name}"]`);
                      radios.forEach((radio) => {
                        if (radio !== input && radio.checked) {
                          radio.checked = false;
                        }
                      });
                    }
                    
                    // Trigger change event to ensure form data is saved
                    input.dispatchEvent(new Event('change', { bubbles: true }));
                    input.dispatchEvent(new Event('input', { bubbles: true }));
                  }
                }, true); // Use capture phase to ensure it runs early
              }
              
              // Use change event which fires after browser updates the checked state
              input.addEventListener('change', function(e) {
                // Ensure this radio is checked (browser should have done this, but ensure it)
                if (!this.checked) {
                  this.checked = true;
                }
                // Uncheck other radios in the same group (browser should do this, but ensure it)
                const name = this.name;
                const form = this.closest('form');
                if (form) {
                  const radios = form.querySelectorAll(`input[name="${name}"]`);
                  radios.forEach((radio) => {
                    if (radio !== this && radio.checked) {
                      radio.checked = false;
                    }
                  });
                }
              });
              
              // Also handle click to ensure immediate visual feedback
              input.addEventListener('click', function(e) {
                // Let browser handle the default behavior, but ensure state after
                setTimeout(() => {
                  if (!this.checked) {
                    this.checked = true;
                    // Force change event
                    this.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                }, 0);
              });
            }
          });
          // Date input is now handled by three separate text fields with keyboard input
          // No date picker needed
        }
      }

      openDatePickerFromBody(e) {
        const realInput = e.target;
        if (realInput.type !== 'date') return;
        
        // Only prevent default on focus to avoid duplicate triggers
        if (e.type === 'focus') {
          return; // Let click handle it
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        // Try direct showPicker first (most reliable if supported)
        if (typeof realInput.showPicker === 'function') {
          try {
            realInput.showPicker();
            return;
          } catch (err) {
            console.log('showPicker failed, trying alternative method:', err);
          }
        }
        
        // Fallback: Create temporary input overlay
        const rect = realInput.getBoundingClientRect();
        const temp = document.createElement('input');
        temp.type = 'date';
        temp.value = realInput.value || '';
        temp.setAttribute('aria-hidden', 'true');
        temp.style.cssText = [
          'position:fixed',
          'left:' + rect.left + 'px',
          'top:' + rect.top + 'px',
          'width:' + Math.max(rect.width, 60) + 'px',
          'height:' + Math.max(rect.height, 40) + 'px',
          'z-index:99999',
          'opacity:0.01',
          'cursor:pointer',
          'border:none',
          'font-size:16px',
          'pointer-events:auto'
        ].join(';');
        
        let isPickerOpen = false;
        
        function cleanup() {
          if (temp.parentNode) {
            temp.parentNode.removeChild(temp);
          }
        }
        
        temp.addEventListener('change', function () {
          isPickerOpen = false;
          realInput.value = temp.value;
          realInput.dispatchEvent(new Event('change', { bubbles: true }));
          realInput.dispatchEvent(new Event('input', { bubbles: true }));
          cleanup();
        });
        
        temp.addEventListener('blur', function () {
          setTimeout(function() {
            if (!isPickerOpen) {
              cleanup();
            }
          }, 500);
        });
        
        document.body.appendChild(temp);
        
        // Use multiple RAF calls for better timing
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            temp.focus();
            isPickerOpen = true;
            
            // Try showPicker with error handling
            if (typeof temp.showPicker === 'function') {
              try {
                temp.showPicker();
              } catch (err) {
                console.log('temp.showPicker failed:', err);
                // Fallback to click
                try {
                  temp.click();
                } catch (clickErr) {
                  console.log('temp.click failed:', clickErr);
                  cleanup();
                }
              }
            } else {
              // No showPicker support, use click
              try {
                temp.click();
              } catch (clickErr) {
                console.log('temp.click failed:', clickErr);
                cleanup();
              }
            }
          });
        });
      }

      addWhatsAppValidation(input) {
        const errorMessage = input.parentElement.querySelector('#whatsapp-error');
        
        // Validate on input - only allow numbers and limit to 10 digits
        input.addEventListener('input', (e) => {
          // Remove any non-numeric characters
          let value = e.target.value.replace(/\D/g, '');
          
          // Limit to 10 digits
          if (value.length > 10) {
            value = value.slice(0, 10);
          }
          
          // Update the input value
          e.target.value = value;
          
          // Show/hide error message
          if (errorMessage) {
            if (value.length > 0 && value.length < 10) {
              errorMessage.style.display = 'block';
            } else {
              errorMessage.style.display = 'none';
            }
          }
        });
        
        // Validate on paste
        input.addEventListener('paste', (e) => {
          e.preventDefault();
          const pastedData = e.clipboardData.getData('text');
          // Remove any non-numeric characters
          let value = pastedData.replace(/\D/g, '');
          
          // Limit to 10 digits
          if (value.length > 10) {
            value = value.slice(0, 10);
          }
          
          input.value = value;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        });
        
        // Prevent non-numeric key presses
        input.addEventListener('keypress', (e) => {
          // Allow: backspace, delete, tab, escape, enter
          if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
              // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
              (e.keyCode === 65 && (e.ctrlKey === true || e.metaKey === true)) ||
              (e.keyCode === 67 && (e.ctrlKey === true || e.metaKey === true)) ||
              (e.keyCode === 86 && (e.ctrlKey === true || e.metaKey === true)) ||
              (e.keyCode === 88 && (e.ctrlKey === true || e.metaKey === true))) {
            return;
          }
          // Ensure that it is a number and stop the keypress if not
          if ((e.which < 48 || e.which > 57)) {
            e.preventDefault();
          }
          // Also prevent if already at max length
          if (input.value.length >= 10) {
            e.preventDefault();
          }
        });
      }

      addEmailValidation(input) {
        const errorMessage = input.parentElement.querySelector('#email-error');
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        
        // Validate on blur (when user leaves the field)
        input.addEventListener('blur', (e) => {
          const value = e.target.value.trim();
          
          if (errorMessage) {
            if (value.length > 0 && !emailRegex.test(value)) {
              errorMessage.style.display = 'block';
            } else {
              errorMessage.style.display = 'none';
            }
          }
        });
        
        // Clear error message on input (user is typing)
        input.addEventListener('input', (e) => {
          if (errorMessage) {
            errorMessage.style.display = 'none';
          }
        });
      }
 
      showSlide(slideNumber, backButton = false, forceShow = false) {
        // Force show (e.g. report list from URL) skips eligibility checks
        // BACKWARD NAVIGATION: Skip eligibility checks for back navigation
        if (!forceShow && !backButton) {
          // FORWARD NAVIGATION: Apply strict validation rules
          // This applies to: initial load, refresh, and forward navigation (Continue)
          // Guard logic (eligibility checks) only blocks FORWARD navigation
          if (!this.canAccessSlide(slideNumber)) {
            // Force navigation to highest valid slide
            const validSlide = this.getHighestValidSlide();
            console.warn(`Cannot access slide ${slideNumber}. Required data missing. Redirecting to slide ${validSlide}.`);
            // Use setTimeout to avoid recursion issues
            setTimeout(() => {
              this.showSlide(validSlide, false);
            }, 0);
            return;
          }
        }
        // If backButton is true, proceed directly without validation
        // This allows unconditional backward navigation - no auto-correction or redirect
        
        // Track if this is an actual slide change (not initial load)
        // Only scroll if component is initialized AND slide number actually changed
        const isSlideChange = this.isInitialized && this.currentSlide !== slideNumber;
        
        // Hide initial loader if we're resolving the initial step
        if (this.isResolvingInitialStep) {
          this.isResolvingInitialStep = false;
          if (this.initialLoader) {
            this.initialLoader.classList.add('skin-assessment__initial-loader--hidden');
          }
        }
        
        this.clearContinueLoader();
        this.slides.forEach((slide, index) => {
          if (index + 1 === slideNumber) {
            slide.classList.remove('skin-assessment__slide--hidden');
            slide.setAttribute('aria-hidden', 'false');
            this.enableFormFieldsInSlide(slide);
            if (slideNumber === 5) {
              if (this.cameFromReportList) {
                slide.classList.add('skin-assessment__slide-5--from-report-list');
                this.renderSlide5CustomerInfo();
              } else {
                slide.classList.remove('skin-assessment__slide-5--from-report-list');
                this.hideSlide5CustomerInfo();
              }
            }
            const state = this.getAssessmentState();
            if (slideNumber === 3 && state.location.latitude && state.location.longitude) {
              // Small delay to ensure widget is initialized
              setTimeout(() => {
                this.updateWidgetLocation(state.location.latitude, state.location.longitude);
              }, 100);
            }
            
            // If showing slide 5 and getTags data exists, restore the report
            if (slideNumber === 5 && state.getTagsResponse) {
              // Small delay to ensure slide is visible
              setTimeout(() => {
                if (typeof buildReportFromResponse === 'function') {
                  buildReportFromResponse(state.getTagsResponse);
                }
                // Initialize Step 5: Auto-select first metric card
                this.initializeStep5();
              }, 150);
            } else if (slideNumber === 5) {
              // Even if report isn't restored, initialize Step 5 if it's already built
              setTimeout(() => {
                this.initializeStep5();
              }, 100);
            }
          } else {
            slide.classList.add('skin-assessment__slide--hidden');
            slide.setAttribute('aria-hidden', 'true');
          }
        });
        this.currentSlide = slideNumber;
        // Note: currentSlide is not persisted - slide selection derived from data only

        // GA4: skin report slide visible
        if (slideNumber === 5) {
          this.dispatchEvent(new CustomEvent('skin-assessment-slide5-visible', { bubbles: true }));
        }

        // Scroll to top on step change (after DOM updates)
        if (isSlideChange) {
          // Use requestAnimationFrame to ensure DOM updates are complete
          requestAnimationFrame(() => {
            // Scroll to top smoothly
            window.scrollTo({
              top: 0,
              left: 0,
              behavior: 'smooth'
            });
          });
        }
      }

      enableFormFieldsInSlide(slide) {
        // Small delay to ensure slide is visible before enabling fields
        setTimeout(() => {
          const form = slide.querySelector('form');
          if (form) {
            const inputs = form.querySelectorAll('input, textarea, select, button');
            inputs.forEach((input) => {
              if (input.disabled) {
                input.disabled = false;
              }
              // Ensure pointer events are enabled
              input.style.pointerEvents = 'auto';
              input.style.opacity = '1';
            });
          }
        }, 50);
      }

      // Initialize Step 5: Auto-select first metric card and set up click handlers
      initializeStep5() {
        const slide5 = this.querySelector('[data-slide="5"]');
        if (!slide5) return;

        // Find all metric cards
        const metricCards = slide5.querySelectorAll('.skin-report__metric-card');
        if (metricCards.length === 0) return;

        // Set up click handlers for all metric cards
        // This works with existing handlers from buildReportFromResponse
        metricCards.forEach((card) => {
          const metricContent = card.querySelector('.skin-report__metric-content');
          if (!metricContent) return;

          // Add click handler for active state management
          // This runs after existing handlers (bubble phase) to update visual state
          metricContent.addEventListener('click', (e) => {
            // Small delay to ensure existing handler runs first, then update active state
            setTimeout(() => {
              this.handleMetricCardClick(card, slide5);
            }, 10);
          }); // Use bubble phase (default) to run after existing handlers
        });

        // Auto-select first card if no card is currently active
        const activeCard = slide5.querySelector('.skin-report__metric-card.is-active');
        if (!activeCard && metricCards.length > 0) {
          this.selectMetricCard(metricCards[0], slide5);
        }
      }

      // Handle metric card click: manage active state only
      // Image updates are handled by existing handlers in buildReportFromResponse
      handleMetricCardClick(clickedCard, slide5) {
        // Remove active state from all cards
        const allCards = slide5.querySelectorAll('.skin-report__metric-card');
        allCards.forEach((card) => {
          card.classList.remove('is-active');
        });

        // Apply active state to clicked card
        clickedCard.classList.add('is-active');
      }

      // Select a metric card programmatically (for auto-selection)
      // Also triggers the image update by simulating a click on the content
      selectMetricCard(card, slide5) {
        // Remove active state from all cards
        const allCards = slide5.querySelectorAll('.skin-report__metric-card');
        allCards.forEach((c) => {
          c.classList.remove('is-active');
        });

        // Apply active state to selected card
        card.classList.add('is-active');

        // Trigger image update by clicking the content element
        // This uses the existing handler from buildReportFromResponse
        const metricContent = card.querySelector('.skin-report__metric-content');
        if (metricContent) {
          // Simulate click to trigger existing image update handler
          metricContent.click();
        }
      }
 
      handleContinue(event) {
        event.preventDefault();
       
        // Validate form based on current slide
        if (this.currentSlide === 1) {
          // STEP 1: Contact Info
          if (!this.validateContactForm()) {
            return;
          }
          this.saveContactFormData();
          
          // Step 1 complete: Allow navigation to Step 2 if phone and email exist
          // Do NOT create customer here
          const step1Complete = this.isStep1Complete();
          if (step1Complete) {
            this.showSlide(2);
            // GA4: fire only after phone + email valid and moving to next slide
            this.dispatchEvent(new CustomEvent('skin-assessment-step1-complete', { bubbles: true }));
          }
          return;
        } else if (this.currentSlide === 2) {
          // STEP 2: Basic Info
          // Validate all required fields including date of birth
          if (!this.validateForm()) {
            return;
          }
          
          // Save form data (all fields validated)
          this.saveFormData();
          
          // GA4: fire only after all fields valid and moving to next slide
          this.dispatchEvent(new CustomEvent('skin-assessment-step2-complete', { bubbles: true }));
          
          // TRANSITION TO STEP 3: Use centralized enterAssessment() function
          // This is the ONLY place that handles customer creation, location resolution, and Slide 3 navigation
          this.enterAssessment();
          return;
        }
 
        // Move to next slide
        if (this.currentSlide < this.slides.length) {
          this.showSlide(this.currentSlide + 1);
        } else {
          // Handle completion - you can add your logic here
          this.handleCompletion();
        }
      }
 
      handleRetake(event) {
        event.preventDefault();
        // Reset photo capture or go back to slide 2
        // You can customize this behavior based on your needs
        if (this.currentSlide === 3) {
          // Reset photo capture area
          const photoPlaceholder = this.querySelector('.skin-assessment__photo-placeholder');
          if (photoPlaceholder) {
            // Add your reset logic here
          }
        }
      }
 
      handleBack(event) {
        event.preventDefault();
       
        // Don't go back if modal is visible
        if (this.locationModal && !this.locationModal.classList.contains('skin-assessment__location-modal--hidden')) {
          return;
        }

        // From slide 5: if we arrived via report list item click, Back goes to slide 8 (report list)
        if (this.currentSlide === 5 && this.cameFromReportList) {
          this.cameFromReportList = false;
          this.showSlide(8, true);
          return;
        }

        // BACKWARD NAVIGATION: Unconditional backward navigation
        // Must allow: Slide 2 → Slide 1, Slide 3 → Slide 2, etc.
        if (this.currentSlide > 1) {
          const previousSlide = this.currentSlide - 1;
         
          // SPECIAL CASE: When going back from slide 4 to slide 3
          // Reset slide 3 to initial state (instructions + start button)
          // Clear base64 and images
          if (this.currentSlide === 4 && previousSlide === 3) {
            this.resetSlide3ToInitialState();
          }
         
          // Pass backButton=true to skip eligibility checks
          // This allows users to edit previous steps without being blocked
          this.showSlide(previousSlide, true);
        } else {
          // On first slide, you might want to navigate away or do nothing
          // You can customize this behavior
        }
      }

      // Reset slide 3 to initial state (instructions + start button)
      // Clears base64 data and images
      resetSlide3ToInitialState() {
        // Clear base64 and getTags data from state
        this.updateAssessmentState({
          captureBase64: null,
          getTagsResponse: null
        });
       
        // Clear window globals for backward compatibility
        window.skinAssessmentCaptureBase64 = null;
        window.skinAssessmentGetTagsResponse = null;
       
        // Clear base64 result display (if function exists from liquid file)
        if (typeof window.clearBase64Result === 'function') {
          window.clearBase64Result();
        }
       
        // Reset slide 3 UI elements
        const slide3 = this.querySelector('[data-slide="3"]');
        if (!slide3) return;
       
        // Show start button
        const startBtn = slide3.querySelector('#btn_start');
        if (startBtn) {
          startBtn.style.display = 'inline-block';
        }
       
        // Hide mirror content
        const mirrorContent = slide3.querySelector('#mirror_content');
        if (mirrorContent) {
          mirrorContent.style.display = 'none';
        }
       
        // Show instruction content
        const instructionContent = slide3.querySelector('.analysis-instruction-data-content');
        if (instructionContent) {
          instructionContent.style.display = 'block';
        }
       
        // Clear canvas container
        const canvasContainer = slide3.querySelector('#canvas_container');
        if (canvasContainer) {
          canvasContainer.innerHTML = '';
          canvasContainer.style.display = 'none';
        }
       
        // Clear captured image
        const capturedImage = slide3.querySelector('#image_captured');
        if (capturedImage) {
          capturedImage.src = '';
          capturedImage.style.display = 'none';
        }
       
        // Clear result sections
        const resultSection = slide3.querySelector('#widget_result_section');
        if (resultSection) {
          resultSection.style.display = 'none';
        }
       
        const resultsContainer = slide3.querySelector('#resutls_container');
        if (resultsContainer) {
          resultsContainer.style.display = 'none';
        }
       
        const onCaptureSuccess = slide3.querySelector('#onCaptureSuccess_1');
        if (onCaptureSuccess) {
          onCaptureSuccess.innerHTML = '';
        }
       
        // Hide profile buttons
        const frontProfile = slide3.querySelector('#front_profile');
        const leftProfile = slide3.querySelector('#left_profile');
        const rightProfile = slide3.querySelector('#right_profile');
        if (frontProfile) frontProfile.style.display = 'none';
        if (leftProfile) leftProfile.style.display = 'none';
        if (rightProfile) rightProfile.style.display = 'none';
       
        // Destroy/reset widget if it exists
        if (typeof window.mirror !== 'undefined' && window.mirror && typeof window.mirror.destroy === 'function') {
          try {
            window.mirror.destroy();
            window.mirror = null;
          } catch (e) {
            console.warn('Error destroying mirror widget:', e);
          }
        }
      }

      setContinueLoader(btn, loading) {
        if (!btn) return;
        if (loading) {
          btn.setAttribute('data-original-text', btn.textContent || 'Continue');
          btn.textContent = '';
          btn.disabled = true;
          btn.classList.add('is-loading');
          let spinner = btn.querySelector('.skin-assessment__btn-spinner');
          if (!spinner) {
            spinner = document.createElement('span');
            spinner.className = 'skin-assessment__btn-spinner';
            spinner.setAttribute('aria-hidden', 'true');
            btn.appendChild(spinner);
          }
        } else {
          btn.classList.remove('is-loading');
          btn.disabled = false;
          const spinner = btn.querySelector('.skin-assessment__btn-spinner');
          if (spinner) spinner.remove();
          const originalText = btn.getAttribute('data-original-text');
          if (originalText != null) btn.textContent = originalText;
          btn.removeAttribute('data-original-text');
        }
      }

      clearContinueLoader() {
        this.querySelectorAll('.skin-assessment__continue-btn.is-loading').forEach((btn) => {
          this.setContinueLoader(btn, false);
        });
      }

      handleBottomNavClick(event) {
        event.preventDefault();
        const navItem = event.currentTarget;
        const navType = navItem.getAttribute('data-nav');
        const navToSlide = {
          'skin-report': 5,
          'products': 6,
          'consultation': 7
        };
        const targetSlide = navToSlide[navType];
        if (targetSlide) {
          const fromReportListFlow = this.cameFromReportList || this.currentSlide === 8;
          const forceShowNav = (targetSlide === 5 || targetSlide === 6) && fromReportListFlow;
          this.showSlide(targetSlide, false, forceShowNav);
          if (navType === 'products') {
            this.showRecommended();
          }
          const allNavItems = this.querySelectorAll('.bottom-nav-item[data-nav]');
          allNavItems.forEach((item) => {
            if (item.getAttribute('data-nav') === navType) {
              item.classList.add('active');
            } else {
              item.classList.remove('active');
            }
          });
        }
      }
showRecommended() {
  const metricCards = document.querySelectorAll('.skin-report__metric-card');
  const scores = {};

  metricCards.forEach((card) => {
    const nameElement = card.querySelector('.skin-report__metric-name');
    const valueElement = card.querySelector('.skin-report__metric-value');

    if (nameElement && valueElement) {
      const concernName = nameElement.textContent.trim();
      const scoreValue = parseFloat(valueElement.textContent.trim());

      if (!isNaN(scoreValue)) {
        scores[concernName] = scoreValue;
      }
    }
  });

  if (Object.keys(scores).length === 0) return;
  if (!window.skinAssessmentData) return;

  const productElements = this.querySelectorAll('[data-product-name]');
  if (productElements.length === 0) return;

  productElements.forEach((productElement) => {
    const productName = productElement.getAttribute('data-product-name');
    const matchResult = this.shouldShowProductByScores(productName, scores);

    if (matchResult.shouldShow) {
      productElement.classList.remove('hidden');
    } else {
      productElement.classList.add('hidden');
    }
  });
}

// Check if a product should be shown by checking ALL concerns and their scores
shouldShowProductByScores(productName, scores) {
  const result = { shouldShow: false, matchedConcerns: [] };

  if (!window.skinAssessmentData || !productName) return result;

  const normalizedProductName = productName.toLowerCase().trim();

  Object.keys(window.skinAssessmentData).forEach((concernType) => {
    const concernData = window.skinAssessmentData[concernType];
    if (!Array.isArray(concernData)) return;

    const concernScore = scores[concernType];
    if (concernScore == null) return;

    for (const scaleRange of concernData) {
      const { min, max } = scaleRange.scale;
      const products = scaleRange.products || [];

      if (concernScore >= min && concernScore <= max) {
        const foundMatch = products.some(
          (p) => p.toLowerCase().trim() === normalizedProductName
        );

        if (foundMatch) {
          result.shouldShow = true;
          result.matchedConcerns.push(concernType);
        }
      }
    }
  });

  return result;
}

// Single concern-based check (kept minimal)
shouldShowProduct(productName, concernType, scores) {
  const result = { shouldShow: false, usage: null };

  if (!window.skinAssessmentData || !productName || !concernType) return result;

  const concernData = window.skinAssessmentData[concernType];
  if (!Array.isArray(concernData)) return result;

  const concernScore = scores[concernType];
  if (concernScore == null) return result;

  const normalizedProductName = productName.toLowerCase().trim();

  for (const scaleRange of concernData) {
    const { min, max } = scaleRange.scale;
    const products = scaleRange.products || [];

    if (concernScore >= min && concernScore <= max) {
      const foundMatch = products.some(
        (p) => p.toLowerCase().trim() === normalizedProductName
      );

      if (foundMatch) {
        result.shouldShow = true;
        return result;
      }
    }
  }

  return result;
}


      // DECISION MAKING: Determine what actions to take based on permission state
      // Returns: Object with action flags
      // NO side effects - pure decision logic
      determineLocationActions(permissionState) {
        const state = this.getAssessmentState();
        const hasStoredLocation = !!(state.location.latitude && state.location.longitude);
        
        switch (permissionState) {
          case 'granted':
            return {
              shouldShowModal: false,
              shouldRequestLocation: false,
              shouldGetLocationSilently: !hasStoredLocation,
              shouldUseStoredLocation: hasStoredLocation,
              shouldProceedToSlide3: true,
              shouldSkipLocation: false,
            };
          
          case 'denied':
            return {
              shouldShowModal: false,
              shouldRequestLocation: false,
              shouldGetLocationSilently: false,
              shouldUseStoredLocation: false,
              shouldProceedToSlide3: true,
              shouldSkipLocation: true,
            };
          
          case 'prompt':
          default:
            return {
              shouldShowModal: true,
              shouldRequestLocation: false,
              shouldGetLocationSilently: false,
              shouldUseStoredLocation: false,
              shouldProceedToSlide3: false,
              shouldSkipLocation: false,
            };
        }
      }

      // SIDE EFFECTS: Execute actions based on decision
      // This function performs all UI and API side effects
      executeLocationActions(actions) {
        const state = this.getAssessmentState();
        
        // Use stored location if available and action says to
        if (actions.shouldUseStoredLocation) {
          this.updateWidgetLocation(state.location.latitude, state.location.longitude);
        }
        
        // Get location silently if permission is granted and we don't have stored location
        if (actions.shouldGetLocationSilently) {
          this.getLocationSilently();
        }
        
        // Show modal if needed
        if (actions.shouldShowModal) {
          this.showLocationModal();
        }
        
        // Update location state if needed (navigation will be handled by enterAssessment() after promise resolves)
        if (actions.shouldProceedToSlide3) {
          if (actions.shouldSkipLocation) {
            // Mark location as skipped
            this.updateAssessmentState({
              location: {
                allowed: false,
                latitude: null,
                longitude: null,
                skipped: true,
                error: 'Permission previously denied'
              }
            });
          }
          // DO NOT call enterAssessment() here - it's already being called from resolveLocationPermissionForAssessment()
          // The navigation will happen automatically in enterAssessment() after the promise resolves
        }
      }

      // Execute location actions and wait for location fetch if needed
      executeLocationActionsAndWait(actions) {
        return new Promise((resolve) => {
          const state = this.getAssessmentState();
          
          // Use stored location if available and action says to
          if (actions.shouldUseStoredLocation) {
            this.updateWidgetLocation(state.location.latitude, state.location.longitude);
            // Location already available, resolve immediately
            resolve();
            return;
          }
          
          // Get location silently if permission is granted and we don't have stored location
          if (actions.shouldGetLocationSilently) {
            // Wait for location to be fetched
            this.getLocationSilently().then(() => {
              resolve();
            }).catch(() => {
              // Even if location fetch fails, resolve to allow navigation
              resolve();
            });
            return;
          }
          
          // Show modal if needed (for prompt state)
          if (actions.shouldShowModal) {
            this.showLocationModal();
          }
          
          // Update location state if needed
          if (actions.shouldProceedToSlide3) {
            if (actions.shouldSkipLocation) {
              // Mark location as skipped
              this.updateAssessmentState({
                location: {
                  allowed: false,
                  latitude: null,
                  longitude: null,
                  skipped: true,
                  error: 'Permission previously denied'
                }
              });
            }
          }
          
          // Resolve immediately for denied state or if no location fetch needed
          resolve();
        });
      }

      // AUTHORITATIVE TRANSITION FUNCTION: enterAssessment()
      // This is the ONLY place allowed to:
      // - Create the customer
      // - Resolve location permission
      // - Navigate to Slide 3
      // Must be idempotent and safe on reload
      enterAssessment() {
        // PRECONDITION 1: Validate Step 1 data exists (phone AND email)
        if (!this.isStep1Complete()) {
          console.warn('enterAssessment: Step 1 data incomplete. Redirecting to Slide 1.');
          this.showSlide(1);
          return;
        }
        
        // PRECONDITION 2: Validate Step 2 data exists (basic info)
        if (!this.isStep2Complete()) {
          console.warn('enterAssessment: Step 2 data incomplete. Redirecting to Slide 2.');
          this.showSlide(2);
          return;
        }
        
        const state = this.getAssessmentState();
        const hasCustomerId = state.customerId && state.customerId.trim().length > 0;
        const locationResolved = this.isLocationDecisionResolved();
        
        // ACTION 1: Create Customer (FIRST action - must happen before location)
        if (!hasCustomerId) {
          const customerData = this.collectCustomerData();
          const continueBtn = this.querySelector('[data-slide="2"] [data-action="next"]');
          if (continueBtn) this.setContinueLoader(continueBtn, true);
          
          this.createCustomer(customerData).then((response) => {
            // Update state with customer ID (even if "unknown")
            // TEMPORARY FALLBACK: customerId may be "unknown" if creation failed
            if (response.customerId) {
              this.updateAssessmentState({ customerId: String(response.customerId) });
            }
            
            // ACTION 2: Resolve Location Permission (SECOND action - after customer exists)
            // Continue even if customerId is "unknown"
            this.resolveLocationPermissionForAssessment().then(() => {
              // ACTION 3: Navigate to highest valid slide (FINAL action - after customer and location resolved)
              // Check for assessment data to determine if user should land on Slide 4 or 5
              const highestSlide = this.getHighestValidSlide();
              this.showSlide(highestSlide);
            });
          }).catch((error) => {
            // This catch should rarely be hit now since createCustomer() returns a response instead of throwing
            // But keep it as a safety net
            console.error('enterAssessment: Unexpected error creating customer:', error);
            // TEMPORARY FALLBACK: Set customerId to "unknown" and continue
            this.updateAssessmentState({ customerId: 'unknown' });
            this.resolveLocationPermissionForAssessment().then(() => {
              const highestSlide = this.getHighestValidSlide();
              this.showSlide(highestSlide);
            });
          }).finally(() => {
            if (continueBtn) this.setContinueLoader(continueBtn, false);
          });
          return;
        }
        
        // Customer already exists, check location
        if (!locationResolved) {
          // ACTION 2: Resolve Location Permission (SECOND action)
          this.resolveLocationPermissionForAssessment().then(() => {
            // ACTION 3: Navigate to highest valid slide (FINAL action)
            // Check for assessment data to determine if user should land on Slide 4 or 5
            const highestSlide = this.getHighestValidSlide();
            this.showSlide(highestSlide);
          });
          return;
        }
        
        // Both customer and location are resolved
        // ACTION 3: Navigate to highest valid slide (FINAL action)
        // Check for assessment data to determine if user should land on Slide 4 or 5
        const highestSlide = this.getHighestValidSlide();
        this.showSlide(highestSlide);
      }

      // Resolve location permission for assessment (called by enterAssessment)
      // Returns a promise that resolves when location decision is made
      resolveLocationPermissionForAssessment() {
        return new Promise((resolve) => {
          // Check if location is already resolved
          if (this.isLocationDecisionResolved()) {
            resolve();
            return;
          }
          
          // DATA COLLECTION: Check permission state
          if (this.locationPermissionState === null) {
            this.checkLocationPermissionState().then((permissionState) => {
              // Store permission state
              this.locationPermissionState = permissionState;
              
              // DECISION MAKING: Determine actions
              const actions = this.determineLocationActions(permissionState);
              
              // SIDE EFFECTS: Execute actions and wait for location if needed
              this.executeLocationActionsAndWait(actions).then(() => {
                resolve();
              });
            }).catch((error) => {
              // ERROR HANDLING ONLY - Log error, set safe default
              console.error('Unexpected error checking permission state:', error);
              this.locationPermissionState = 'prompt';
              
              // DECISION MAKING: Determine actions with default state
              const actions = this.determineLocationActions('prompt');
              
              // SIDE EFFECTS: Execute actions
              this.executeLocationActions(actions);
              
              // Resolve promise
              resolve();
            });
          } else {
            // DECISION MAKING: Determine actions based on current state
            const actions = this.determineLocationActions(this.locationPermissionState);
            
            // SIDE EFFECTS: Execute actions and wait for location if needed
            this.executeLocationActionsAndWait(actions).then(() => {
              resolve();
            });
          }
        });
      }

      // Handle location permission flow based on permission state
      handleLocationPermissionFlow() {
        // DATA COLLECTION: Ensure permission state is checked
        if (this.locationPermissionState === null) {
          // Check permission state (never throws)
          this.checkLocationPermissionState().then((permissionState) => {
            // Store permission state
            this.locationPermissionState = permissionState;
            
            // DECISION MAKING: Determine actions
            const actions = this.determineLocationActions(permissionState);
            
            // SIDE EFFECTS: Execute actions
            this.executeLocationActions(actions);
          }).catch((error) => {
            // ERROR HANDLING ONLY - Log error, set safe default
            console.error('Unexpected error checking permission state:', error);
            this.locationPermissionState = 'prompt';
            
            // DECISION MAKING: Determine actions with default state
            const actions = this.determineLocationActions('prompt');
            
            // SIDE EFFECTS: Execute actions
            this.executeLocationActions(actions);
          });
          return;
        }

        // DECISION MAKING: Determine actions based on current state
        const actions = this.determineLocationActions(this.locationPermissionState);
        
        // SIDE EFFECTS: Execute actions
        this.executeLocationActions(actions);
      }

      showLocationModal() {
        // Only show modal if permission state is "prompt"
        // This ensures we never show modal for "granted" or "denied" states
        if (this.locationPermissionState !== 'prompt') {
          console.warn('Location modal should only be shown when permission state is "prompt"');
          return;
        }

        if (this.locationModal) {
          this.locationModal.classList.remove('skin-assessment__location-modal--hidden');
          document.body.style.overflow = 'hidden'; // Prevent background scrolling
          // Hide all back buttons when modal is visible
          this.backButtons.forEach((btn) => {
            btn.style.display = 'none';
          });
        }
      }
 
      hideLocationModal() {
        if (this.locationModal) {
          this.locationModal.classList.add('skin-assessment__location-modal--hidden');
          document.body.style.overflow = ''; // Restore scrolling
          // Show back buttons again when modal is hidden
          this.backButtons.forEach((btn) => {
            btn.style.display = 'flex';
          });
        }
      }

      // Show consultation booking modal
      showConsultationModal(productUrl) {
        if (!this.consultationModal) return;

        // Store product data for later use
        // If productUrl is provided, it's the legacy method (will fetch variant)
        // Otherwise, variantId should already be set in currentBookingProduct
        if (productUrl) {
          this.currentBookingProduct = { url: productUrl };
        }
        // If no productUrl, currentBookingProduct should already have variantId

        // Load modal text from data attributes
        // Load modal text from data attributes
        const feeText =
          this.consultationModal.getAttribute('data-consultation-fee-text') ||
          '₹500 consultation fee';

        const infoText =
          this.consultationModal.getAttribute('data-consultation-info-text') ||
          '<p>Our team will contact you within 24 hours to confirm your appointment.</p>';

        // Update modal content
        const feeEl = this.consultationModal.querySelector('#consultation-modal-fee');
        const infoEl = this.consultationModal.querySelector('#consultation-modal-info');

        if (feeEl) feeEl.textContent = feeText;   // plain text ✔
        if (infoEl) infoEl.innerHTML = infoText;  // richtext ✔

        // Show modal
        this.consultationModal.classList.remove('skin-assessment__location-modal--hidden');
        document.body.style.overflow = 'hidden'; // Prevent background scrolling
        // Hide all back buttons when modal is visible
        this.backButtons.forEach((btn) => {
          btn.style.display = 'none';
        });

        // Add overlay click handler to close modal
        const overlay = this.consultationModal.querySelector('.skin-assessment__location-modal-overlay');
        if (overlay) {
          overlay.onclick = (e) => {
            if (e.target === overlay) {
              this.hideConsultationModal();
            }
          };
        }
      }

      // Hide consultation booking modal
      hideConsultationModal() {
        if (this.consultationModal) {
          this.consultationModal.classList.add('skin-assessment__location-modal--hidden');
          document.body.style.overflow = ''; // Restore scrolling
          // Show back buttons again when modal is hidden
          this.backButtons.forEach((btn) => {
            btn.style.display = 'flex';
          });
        }
        this.currentBookingProduct = null;
      }

      // Handle Book Now button click
      handleBookNowClick(event) {
        event.preventDefault();
        const button = event.currentTarget;
        
        // Read variant ID from data attribute (preferred method)
        const variantId = button.getAttribute('data-variant-id');
        const productId = button.getAttribute('data-product-id');
        const dermatName = button.getAttribute('data-doctor-name') || '';
        
        // Fallback to legacy product URL if variant ID not available
        const productUrl = button.getAttribute('data-link');
        
        if (variantId) {
          // Use variant ID from data attribute (new method)
          this.currentBookingProduct = {
            variantId: variantId,
            productId: productId,
            dermatName: dermatName
          };
          this.showConsultationModal();
        } else if (productUrl) {
          // Fallback to legacy method (fetch variant from URL)
          this.showConsultationModal(productUrl);
          if (this.currentBookingProduct) this.currentBookingProduct.dermatName = dermatName;
        } else {
          console.warn('Book Now button missing product variant ID or link');
          return;
        }
      }

      // Handle consultation confirmation
      async handleConsultationConfirm(event) {
        event.preventDefault();
        
        if (!this.currentBookingProduct) {
          console.error('No product data for consultation booking');
          this.hideConsultationModal();
          return;
        }

        // Disable button during processing
        if (this.consultationConfirmBtn) {
          this.consultationConfirmBtn.disabled = true;
          const originalText = this.consultationConfirmBtn.textContent;
          this.consultationConfirmBtn.textContent = 'Processing...';
        }

        try {
          // Get variant ID: prefer from data attribute, fallback to fetching from URL
          let variantId = this.currentBookingProduct.variantId;
          
          if (!variantId && this.currentBookingProduct.url) {
            // Legacy method: fetch variant ID from product URL
            variantId = await this.getFirstAvailableVariantId(this.currentBookingProduct.url);
          }
          
          if (!variantId) {
            throw new Error('No available variant found for this product');
          }

          // Get assessment state for line item properties
          const state = this.getAssessmentState();
          const consultationNoteText = this.consultationModal.getAttribute('data-consultation-note-text') || 
                                       'Our team will contact you within 24 hours to confirm your appointment.';

          // Prepare line item properties
          // TEMPORARY FALLBACK: Only include customerId if it's not "unknown"
          // TODO: Remove this check once backend returns customerId for existing customers
          const dermatName = this.currentBookingProduct?.dermatName || '';
          const properties = {
            // Hidden properties (prefixed with _)
            '_customer_id': (state.customerId && state.customerId !== 'unknown') ? state.customerId : '',
            '_mobile_number': state.formData?.whatsappNumber || '',
            '_email': state.formData?.email || '',
            // Visible properties
            'Dermat Consultation': 'true',
            'Consultation Note': consultationNoteText,
            'Dermat Name': dermatName
          };

          // Add to cart
          await this.addConsultationToCart(variantId, properties);
          window.location.href = '/cart';

        } catch (error) {
          console.error('Error booking consultation:', error);
          alert('Error booking consultation. Please try again.');
        } finally {
          // Re-enable button
          if (this.consultationConfirmBtn) {
            this.consultationConfirmBtn.disabled = false;
            this.consultationConfirmBtn.textContent = 'Confirm Booking';
          }
        }
      }

      // Handle consultation cancellation
      handleConsultationCancel(event) {
        event.preventDefault();
        this.hideConsultationModal();
      }

      // Get first available variant ID from product URL
      async getFirstAvailableVariantId(productUrl) {
        try {
          // Fetch product data from Shopify
          // Convert product URL to product handle
          // Handle both full URLs and relative paths
          let productHandle = productUrl;
          
          // If it's a full URL, extract the handle
          if (productUrl.startsWith('http://') || productUrl.startsWith('https://')) {
            try {
              const url = new URL(productUrl);
              const pathParts = url.pathname.split('/').filter(p => p);
              productHandle = pathParts[pathParts.length - 1];
            } catch (e) {
              // Fallback: try to extract from path
              const urlParts = productUrl.split('/');
              productHandle = urlParts[urlParts.length - 1].split('?')[0];
            }
          } else {
            // Relative path: remove leading/trailing slashes and extract handle
            productHandle = productUrl.replace(/^\/+|\/+$/g, '').split('/').pop().split('?')[0];
          }
          
          if (!productHandle) {
            throw new Error('Invalid product URL');
          }
          
          // Fetch product JSON
          const productJsonUrl = `/products/${productHandle}.js`;
          const response = await fetch(productJsonUrl);
          
          if (!response.ok) {
            throw new Error(`Failed to fetch product: ${response.status}`);
          }

          const product = await response.json();
          
          // Find first available variant
          if (product.variants && product.variants.length > 0) {
            // Look for first available variant (not sold out)
            const availableVariant = product.variants.find(v => v.available) || product.variants[0];
            return availableVariant.id.toString();
          }

          throw new Error('No variants found for this product');
        } catch (error) {
          console.error('Error getting variant ID:', error);
          throw error;
        }
      }

      // Add consultation to cart with variant ID and properties
      async addConsultationToCart(variantId, properties) {
        try {
          // Get cart element for sections rendering
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          
          // Prepare request body
          const body = {
            id: variantId,
            quantity: 1,
            properties: properties
          };

          // Add to cart
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body)
          });

          const data = await response.json();

          if (response.status && response.status !== 200) {
            throw new Error(data.description || 'Failed to add to cart');
          }
          return data;
        } catch (error) {
          console.error('Error adding to cart:', error);
          throw error;
        }
      }
 
      handleLocationAllow(event) {
        event.preventDefault();
       
        // STAGE 1: User explicitly clicks "Yes / Allow" in our custom popup
        // NOW we trigger navigator.geolocation.getCurrentPosition to show the browser popup
        // This is the ONLY place where browser geolocation is requested
        
        if (navigator.geolocation) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              // Browser permission granted
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              
              // Update permission state
              this.locationPermissionState = 'granted';
              
              // Update state with location data
              this.updateAssessmentState({
                location: {
                  allowed: true,
                  latitude: lat,
                  longitude: lng,
                  skipped: false,
                  error: null
                }
              });
              
              // Sync formData for backward compatibility
              this.formData = {
                ...this.formData,
                locationAllowed: true,
                latitude: lat,
                longitude: lng,
              };
              
              // Update widget location variables
              this.updateWidgetLocation(lat, lng);
              
              this.hideLocationModal();
              // Use enterAssessment() to navigate to Slide 3 (ensures all preconditions are met)
              this.enterAssessment();
            },
            (error) => {
              // Browser permission denied or error
              console.warn('Location access denied or error:', error);
              
              // Update permission state
              if (error.code === error.PERMISSION_DENIED) {
                this.locationPermissionState = 'denied';
              }
              
              // Update state with error
              this.updateAssessmentState({
                location: {
                  allowed: false,
                  latitude: null,
                  longitude: null,
                  skipped: false,
                  error: error.message
                }
              });
              
              // Sync formData for backward compatibility
              this.formData = {
                ...this.formData,
                locationAllowed: false,
                locationError: error.message,
              };
              
              this.hideLocationModal();
              // Use enterAssessment() to navigate to Slide 3 (ensures all preconditions are met)
              this.enterAssessment();
            }
          );
        } else {
          // Geolocation not supported
          this.locationPermissionState = 'denied';
          
          // Update state
          this.updateAssessmentState({
            location: {
              allowed: false,
              latitude: null,
              longitude: null,
              skipped: false,
              error: 'Geolocation not supported'
            }
          });
          
          // Sync formData for backward compatibility
          this.formData = {
            ...this.formData,
            locationAllowed: false,
            locationError: 'Geolocation not supported',
          };
          
          this.hideLocationModal();
          // Use enterAssessment() to navigate to Slide 3 (ensures all preconditions are met)
          this.enterAssessment();
        }
      }
 
      handleLocationSkip(event) {
        event.preventDefault();
        
        // STAGE 4: User explicitly declines inside our custom popup
        // Treat this as an optional feature skip
        // Do NOT trigger browser permission
        // Continue the flow without location
        
        // Update state
        this.updateAssessmentState({
          location: {
            allowed: false,
            latitude: null,
            longitude: null,
            skipped: true,
            error: null
          }
        });
        
        // Sync formData for backward compatibility
        this.formData = {
          ...this.formData,
          locationAllowed: false,
          locationSkipped: true,
        };
        
        this.hideLocationModal();
        // Use enterAssessment() to navigate to Slide 3 (ensures all preconditions are met)
        // Location is optional, so user can still proceed to assessment
        this.enterAssessment();
      }
 
      // Deprecated: Use updateAssessmentState instead
      // Kept for backward compatibility
      saveLocationData() {
        // State is auto-persisted via updateAssessmentState
        // This method is kept for compatibility but does nothing
      }

      // DATA COLLECTION: Check geolocation permission state WITHOUT requesting permission
      // Returns: 'granted', 'denied', or 'prompt'
      // NEVER throws - always returns a valid state
      // NO side effects - pure data collection function
      async checkLocationPermissionState() {
        // Use Permissions API if available (doesn't trigger permission prompt)
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const result = await navigator.permissions.query({ name: 'geolocation' });
            const state = result.state;
            // Validate state is one of the expected values
            if (state === 'granted' || state === 'denied' || state === 'prompt') {
              return state;
            }
            // If state is unexpected, default to 'prompt'
            console.warn('Unexpected permission state:', state);
            return 'prompt';
        } catch (e) {
            // Error occurred - log it but return neutral state
            // This is NOT a business decision, just error handling
            console.warn('Permissions API query failed:', e);
            // Return neutral state - decision logic will handle it
            return 'prompt';
          }
        }
        
        // Fallback: check if geolocation is supported
        if (!navigator.geolocation) {
          return 'denied';
        }
        
        // If Permissions API not available, we can't check without requesting
        // Return 'prompt' as neutral state
        return 'prompt';
      }

      // Get location silently if permission is already granted (doesn't show prompt)
      // STAGE 2: Only called when permission state is "granted"
      // This should NOT show a browser prompt since permission is already granted
      getLocationSilently() {
        return new Promise((resolve, reject) => {
          // Double-check: Only proceed if permission state is "granted"
          if (this.locationPermissionState !== 'granted') {
            console.warn('getLocationSilently called but permission state is not "granted"');
            reject(new Error('Permission state is not granted'));
            return;
          }
          
          if (!navigator.geolocation) {
            reject(new Error('Geolocation not available'));
            return;
          }
          
          // Only call if permission is already granted (shouldn't show prompt)
          // Note: In rare cases where permission was just revoked, this might fail silently
          navigator.geolocation.getCurrentPosition(
            (position) => {
              const lat = position.coords.latitude;
              const lng = position.coords.longitude;
              
              // Update state
              this.updateAssessmentState({
                location: {
                  allowed: true,
                  latitude: lat,
                  longitude: lng,
                  skipped: false,
                  error: null
                }
              });
              
              // Sync formData for backward compatibility
              this.formData = {
                ...this.formData,
                locationAllowed: true,
                latitude: lat,
                longitude: lng,
              };
              
              this.updateWidgetLocation(lat, lng);
              resolve();
            },
            (error) => {
              // Silent failure - permission might have been revoked
              // Update permission state if permission was denied
              if (error.code === error.PERMISSION_DENIED) {
                this.locationPermissionState = 'denied';
              }
              console.warn('Silent location fetch failed:', error);
              reject(error);
            },
            { timeout: 5000 } // Short timeout for silent fetch
          );
        });
      }

      // Update widget's location variables (called from liquid file script)
      updateWidgetLocation(lat, lng) {
        // Update state
        this.updateAssessmentState({
          location: {
            ...this.getAssessmentState().location,
            latitude: lat,
            longitude: lng
          }
        });
        
        // Update widget's global location variables if function exists
        if (typeof window.updateSkinAssessmentLocation === 'function') {
          window.updateSkinAssessmentLocation(lat, lng);
        }
        
        // Also set window variables directly as fallback (for backward compatibility)
        window.skinAssessmentLatitude = lat;
        window.skinAssessmentLongitude = lng;
      }
 
      // Collect data from slide 1 and slide 2
      collectCustomerData() {
        const contactData = {};
        const formData = {};
 
        // Collect slide 1 data (contact form)
        if (this.contactForm) {
          contactData.whatsappNumber = this.contactForm.querySelector('#whatsapp-number')?.value || '';
          contactData.email = this.contactForm.querySelector('#email-address')?.value || '';
        }
 
        // Collect slide 2 data (main form)
        if (this.form) {
          formData.name = this.form.querySelector('#user-name')?.value || '';
          formData.dateOfBirth = this.form.querySelector('#date-of-birth')?.value || '';
          formData.allergies = this.form.querySelector('input[name="allergies"]:checked')?.value || '';
        }
 
        // Combine both sets of data
        return {
          ...contactData,
          ...formData,
        };
      }
 
      // Fake API call to submit customer data
      async submitCustomerData() {
        const customerData = this.collectCustomerData();

        const continueBtn = this.querySelector('[data-slide="2"] [data-action="next"]');
        if (continueBtn) this.setContinueLoader(continueBtn, true);

        try {
          // Call real API endpoint to create customer
          const response = await this.createCustomer(customerData);
          
          // Store customer ID in state
          if (response.customerId) {
            // Ensure customerId is a string
            const customerId = String(response.customerId);
            
            // Update state with customer ID and form data
            this.updateAssessmentState({
              customerId: customerId,
              formData: {
              ...this.formData,
              customerId: customerId,
              }
            });
            
            // Sync formData for backward compatibility
            this.formData = {
              ...this.formData,
                customerId: customerId,
            };
          } else {
            console.warn('No customer ID in response:', response);
          }
 
          return response;
        } catch (error) {
          console.error('API Error:', error);
          throw error;
        } finally {
          if (continueBtn) this.setContinueLoader(continueBtn, false);
        }
      }
 
      // Real API call to create customer
      async createCustomer(customerData) {
        try {
          // Format dateOfBirth to YYYY-MM-DD before sending
          const formattedData = { ...customerData };
          // if (formattedData.dateOfBirth) {
          //   const date = new Date(formattedData.dateOfBirth);
          //   if (!isNaN(date.getTime())) {
          //     const year = date.getFullYear();
          //     const month = String(date.getMonth() + 1).padStart(2, '0');
          //     const day = String(date.getDate()).padStart(2, '0');
          //     formattedData.dateOfBirth = `${year}-${month}-${day}`;
          //   }
          // }

          if (formattedData.dateOfBirth) {
            const parts = formattedData.dateOfBirth.split(/[-/]/);
            if (parts.length === 3) {
              const [day, month, year] = parts.map(p => p.trim());
              if (
                day.length <= 2 &&
                month.length <= 2 &&
                year.length === 4
              ) {
                formattedData.dateOfBirth = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
              }
            }
          }
          
          const response = await fetch('https://skinq-middleware.lucentinnovation.com/api/customer/create', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(formattedData)
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ message: 'Failed to create customer' }));
            throw new Error(errorData.message || `API Error: ${response.status} ${response.statusText}`);
          }

          const data = await response.json();
          
          // Check if request was successful
          if (data.success === true && data.data && data.data.id) {
            // API response structure: { success: true, message: "...", data: { id: "..." } }
            const customerId = String(data.data.id);
            return {
              success: true,
              customerId: customerId,
              message: data.message || 'Customer created successfully',
              data: customerData,
            };
          } else if (data.customerId || data.id || data.customer_id) {
            // Fallback for other response formats
            const customerId = String(data.customerId || data.id || data.customer_id);
            return {
              success: true,
              customerId: customerId,
              message: data.message || 'Customer created successfully',
              data: customerData,
            };
          } else {
            console.error('No customer ID found in response. Response structure:', data);
            throw new Error(data.message || 'Failed to create customer - no customer ID received');
          }
        } catch (error) {
          console.error('API Error:', error);
          // TEMPORARY FALLBACK: If customer creation fails for ANY reason
          // (including customer already exists, 4xx/5xx, timeout),
          // set customerId to "unknown" and continue assessment flow
          // TODO: Remove this fallback once backend returns customerId for existing customers
          console.warn('Customer creation failed. Using fallback: customerId = "unknown"');
          return {
            success: false,
            customerId: 'unknown',
            error: error.message || 'Failed to create customer',
            message: 'Assessment will continue without customer ID',
            data: customerData,
          };
        }
      }
 
      validateContactForm() {
        if (!this.contactForm) return true;

        const whatsappInput = this.contactForm.querySelector('#whatsapp-number');
        const whatsappNumber = whatsappInput.value.trim();
        const whatsappErrorMessage = whatsappInput.parentElement.querySelector('#whatsapp-error');

        // Basic validation for WhatsApp
        if (!whatsappNumber) {
          alert('Please enter your WhatsApp number');
          if (whatsappErrorMessage) whatsappErrorMessage.style.display = 'none';
          return false;
        }

        // Validate exactly 10 digits and only numbers
        const phoneRegex = /^\d{10}$/;
        if (!phoneRegex.test(whatsappNumber)) {
          alert('Please enter a valid 10-digit WhatsApp number (numbers only)');
          if (whatsappErrorMessage) whatsappErrorMessage.style.display = 'block';
          return false;
        }

        if (whatsappErrorMessage) whatsappErrorMessage.style.display = 'none';

        // Email validation
        const emailInput = this.contactForm.querySelector('#email-address');
        const email = emailInput.value.trim();
        const emailErrorMessage = emailInput.parentElement.querySelector('#email-error');

        // Basic validation for email
        if (!email) {
          alert('Please enter your email address');
          if (emailErrorMessage) emailErrorMessage.style.display = 'none';
          return false;
        }

        // Validate email format
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(email)) {
          alert('Please enter a valid email address');
          if (emailErrorMessage) emailErrorMessage.style.display = 'block';
          return false;
        }

        if (emailErrorMessage) emailErrorMessage.style.display = 'none';
        return true;
      }
 
      validateForm() {
        if (!this.form) return true;
 
        const name = this.form.querySelector('#user-name').value.trim();
        const dateOfBirth = this.form.querySelector('#date-of-birth').value.trim();
        const allergies = this.form.querySelector('input[name="allergies"]:checked');
 
        // Basic validation
        if (!name) {
          alert('Please enter your name');
          return false;
        }
 
        // Check if date fields are filled
        const dayInput = this.form.querySelector('#date-day');
        const monthInput = this.form.querySelector('#date-month');
        const yearInput = this.form.querySelector('#date-year');
        const day = dayInput?.value?.trim() || '';
        const month = monthInput?.value?.trim() || '';
        const year = yearInput?.value?.trim() || '';
        
        if (!day || !month || !year || year.length !== 4 || !dateOfBirth) {
          // Show error message in the date input error element
          const errorElement = document.getElementById('date-input-error');
          if (errorElement) {
            if (!day || !month || !year || year.length !== 4) {
              errorElement.textContent = 'Please enter your date of birth';
            } else {
              errorElement.textContent = 'Please enter a valid date of birth';
            }
            errorElement.style.display = 'block';
            const dateWrapper = dayInput?.closest('.date-input-wrapper');
            if (dateWrapper) {
              dateWrapper.classList.add('has-error');
            }
          }
          // Focus on the first date field
          if (dayInput) {
            dayInput.focus();
          }
          return false;
        }

        // Validate that the date is not in the future and is valid
        const dateParts = dateOfBirth.split(/[-/]/);
        if (dateParts.length === 3) {
          const year = parseInt(dateParts[0]);
          const month = parseInt(dateParts[1]) - 1; // Month is 0-indexed
          const day = parseInt(dateParts[2]);
          const enteredDate = new Date(year, month, day);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          // Check if date is valid
          if (enteredDate.getDate() !== day || enteredDate.getMonth() !== month || enteredDate.getFullYear() !== year) {
            // Show error message in the date input error element
            const dayInput = this.form.querySelector('#date-day');
            const errorElement = document.getElementById('date-input-error');
            if (errorElement) {
              errorElement.textContent = 'Please enter a valid date.';
              errorElement.style.display = 'block';
              const dateWrapper = dayInput?.closest('.date-input-wrapper');
              if (dateWrapper) {
                dateWrapper.classList.add('has-error');
              }
            }
            if (dayInput) {
              dayInput.focus();
            }
            return false;
          }
          
          // Check if date is in the future
          if (enteredDate > today) {
            // Show error message in the date input error element
            const dayInput = this.form.querySelector('#date-day');
            const errorElement = document.getElementById('date-input-error');
            if (errorElement) {
              errorElement.textContent = 'Please enter a valid birth date. Future dates are not allowed.';
              errorElement.style.display = 'block';
              const dateWrapper = dayInput?.closest('.date-input-wrapper');
              if (dateWrapper) {
                dateWrapper.classList.add('has-error');
              }
            }
            if (dayInput) {
              dayInput.focus();
            }
            return false;
          }
        }
  
        if (!allergies) {
          alert('Please indicate if you have skin allergies');
          return false;
        }
  
        return true;
      }
 
      saveContactFormData() {
        if (!this.contactForm) return;
 
        const whatsappNumber = this.contactForm.querySelector('#whatsapp-number').value;
        const email = this.contactForm.querySelector('#email-address').value;
 
        // Merge with existing form data
        const updatedFormData = {
          ...this.formData,
          whatsappNumber: whatsappNumber || '',
          email: email || '',
        };
 
        // Update state (auto-persists)
        this.updateAssessmentState({ formData: updatedFormData });
        
        // Sync formData for backward compatibility
        this.formData = updatedFormData;
      }
 
      saveFormData() {
        if (!this.form) return;
 
        // Also get radio button values
        const name = this.form.querySelector('#user-name')?.value;
        const dateOfBirth = this.form.querySelector('#date-of-birth')?.value;
        const allergies = this.form.querySelector('input[name="allergies"]:checked')?.value;
 
        // Merge with existing form data
        const updatedFormData = {
          ...this.formData,
          name: name || '',
          dateOfBirth: dateOfBirth || '',
          allergies: allergies || '',
        };
 
        // Update state (auto-persists)
        this.updateAssessmentState({ formData: updatedFormData });
        
        // Sync formData for backward compatibility
        this.formData = updatedFormData;
      }
 
      // Deprecated: Use getAssessmentState() instead
      // Kept for backward compatibility
      loadFormData() {
        const state = this.getAssessmentState();
        return state.formData;
      }

      // Deprecated: State is now loaded in loadAssessmentState()
      // Kept for backward compatibility
      restoreAssessmentData() {
        // State is already loaded in loadAssessmentState()
        // This method is kept for compatibility but does nothing
      }

      // Check if Step 1 (Contact Info) is complete
      isStep1Complete() {
        const state = this.getAssessmentState();
        const formData = state.formData || {};
        
        // Step 1 requirements: WhatsApp number and email
        const hasWhatsApp = formData.whatsappNumber && formData.whatsappNumber.trim().length >= 10;
        const hasEmail = formData.email && formData.email.trim().length > 0;
        
        return hasWhatsApp && hasEmail;
      }

      // Check if Step 2 (Basic Info) is complete - name is required
      isStep2Complete() {
        const state = this.getAssessmentState();
        const formData = state.formData || {};
        
        // Step 2 requirement: name (required for assessment start)
        const hasName = formData.name && formData.name.trim().length > 0;
        
        return hasName;
      }

      // Check if required form data is complete (Step 1 and Step 2)
      isFormComplete() {
        return this.isStep1Complete() && this.isStep2Complete();
      }

      // Check if location decision is resolved (allowed or skipped)
      isLocationDecisionResolved() {
        const state = this.getAssessmentState();
        // Location decision is resolved if:
        // - Location was allowed (has coordinates)
        // - Location was skipped (skipped flag is true)
        // - Location was denied (error exists)
        return state.location.allowed || 
               state.location.skipped || 
               (state.location.error && state.location.error.length > 0);
      }

      // Handle recovery flow on page reload
      // Follows recovery rules: check data → create customer if needed → resolve location → proceed
      // Derives starting slide from data in this order:
      // 1. If Step 1 data missing → Slide 1
      // 2. Else if Step 2 data missing → Slide 2
      // 3. Else if customerId missing → call enterAssessment()
      // 4. Else if captureBase64 AND getTagsResponse exist → Slide 5
      // 5. Else if captureBase64 exists → Slide 4
      // 6. Else → Slide 3
      handleRecoveryFlow() {
        const state = this.getAssessmentState();
        const step1Complete = this.isStep1Complete();
        const step2Complete = this.isStep2Complete();
        const hasCustomerId = state.customerId && state.customerId.trim().length > 0;
        const hasBase64 = state.captureBase64 && state.captureBase64.length > 0;
        const hasGetTags = state.getTagsResponse && typeof state.getTagsResponse === 'object';
        
        // RECOVERY RULE 1: If phone/email missing → Step 1
        if (!step1Complete) {
          this.showSlide(1);
          return;
        }
        
        // RECOVERY RULE 2: Else if basic info missing → Step 2
        if (!step2Complete) {
          this.showSlide(2);
          return;
        }
        
        // RECOVERY RULE 3: Else if customerId missing → call enterAssessment()
        // enterAssessment() will create customer, resolve location, and navigate
        if (!hasCustomerId) {
          this.enterAssessment();
          return;
        }
        
        // At this point, forms are complete and customerId exists
        // Check for assessment data to determine highest valid slide
        
        // RECOVERY RULE 4: If captureBase64 AND getTagsResponse exist → Slide 5
        if (hasBase64 && hasGetTags) {
          this.showSlide(5);
          return;
        }
        
        // RECOVERY RULE 5: Else if captureBase64 exists → Slide 4
        if (hasBase64) {
          this.showSlide(4);
          return;
        }
        
        // RECOVERY RULE 6: Else → Slide 3
        // But first, ensure location is resolved (required for Slide 3)
        const locationResolved = this.isLocationDecisionResolved();
        if (!locationResolved) {
          // Location not resolved, need to resolve it first
          // This will eventually navigate to Slide 3 after location is resolved
          this.resolveLocationPermissionForAssessment().then(() => {
            this.showSlide(3);
          });
        } else {
          // Location resolved, navigate to Slide 3
          this.showSlide(3);
        }
      }

      getCustomerIdFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const id = params.get('customerId');
        return (id && typeof id === 'string') ? id : null;
      }

      async fetchCustomerByQueryParam() {
        const customerId = this.getCustomerIdFromUrl();
        if (!customerId) return null;
        try {
          const url =  `https://skinq-middleware.lucentinnovation.com/api/customer/get?id=${encodeURIComponent(customerId)}`;
          const response = await fetch(url, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          });
          const data = await response.json();
          if (!response.ok) {
            console.error('Customer API failed:', response.status);
            this.renderReportListError(data?.message || data?.error || 'Unable to load reports. Please try again later.');
            return null;
          }
          return data;
        } catch (err) {
          console.error('Error fetching customer data:', err);
          this.renderReportListError(err.message || err.response.message ||'Unable to load reports. Please try again later.');
          return null;
        }
      }

      async handleReportListFromUrl() {
        const data = await this.fetchCustomerByQueryParam();
      
        window.reportsResponse = data;
        if (this.isResolvingInitialStep) {
          this.isResolvingInitialStep = false;
          if (this.initialLoader) {
            this.initialLoader.classList.add('skin-assessment__initial-loader--hidden');
          }
        }

        this.showSlide(8, false, true);
        data && this.renderCustomerReportList(data);
      }

      renderReportListError(message) {
        const container = this.querySelector('.report-list__items') || this.querySelector('.report-list-items');
        if (!container) return;
        container.innerHTML = `<p class="report-list__error">${message}</p>`;
      }

      renderCustomerReportList(data) {
        const container = this.querySelector('.report-list__items') || this.querySelector('.report-list-items');
        if (!container) return;
        const payload = data?.data;
        if (!payload?.metafields?.edges || !Array.isArray(payload.metafields.edges)) {
          container.innerHTML = '<p class="report-list__empty">No reports found.</p>';
          return;
        }
        const imageIdsEdge = payload.metafields.edges.find(e => e?.node?.key === 'image_ids');
        if (!imageIdsEdge) {
          container.innerHTML = '<p class="report-list__empty">No reports found.</p>';
          return;
        }
        let reports = [];
        try {
          reports = JSON.parse(imageIdsEdge.node.value);
        } catch (e) {
          console.error('Failed to parse image_ids JSON');
          container.innerHTML = '<p class="report-list__empty">Unable to load reports.</p>';
          return;
        }
        if (!Array.isArray(reports) || !reports.length) {
          container.innerHTML = '<p class="report-list__empty">No reports yet.</p>';
          return;
        }
        const fragment = document.createDocumentFragment();
        reports.forEach((report, index) => {
          let imageId = '';
          let timestamp = '';
          if (typeof report === 'string') {
            imageId = report;
          } else if (report && typeof report === 'object') {
            imageId = report.ImageID || report.ImageId || '';
            timestamp = report.TimeStamp || report.timestamp || '';
          }
          const item = document.createElement('div');
          item.className = 'report-list__item';
          item.setAttribute('role', 'button');
          item.setAttribute('tabindex', '0');
          item.dataset.imageId = imageId;
          item.dataset.timestamp = timestamp;
          item.innerHTML = `
            <span class="report-list__item-spinner-wrap" aria-hidden="true">
              <div class="loading__spinner hidden">
                <svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>
              </div>
            </span>
            <div class="report-list__item-body">
              <span class="report-list__item-title">Report ${index + 1}</span>
              ${timestamp ? `<span class="report-list__item-meta">${timestamp}</span>` : ''}
            </div>
          `;
          item.addEventListener('click', () => {
            if (item.classList.contains('report-list__item--loading')) return;
            this.fetchTagsAndShowReport(imageId, item);
          });
          fragment.appendChild(item);
        });
        container.innerHTML = '';
        container.appendChild(fragment);
      }

      async fetchTagsAndShowReport(imageId, itemElement) {
        if (!imageId) return;
        const DEEPTAG_REQUIRED_TAGS = window.DEEPTAG_REQUIRED_TAGS;
        if (!DEEPTAG_REQUIRED_TAGS) {
          console.error('DEEPTAG_REQUIRED_TAGS not defined');
          return;
        }
        if (itemElement) {
          this.currentReportTimestamp = itemElement.dataset.timestamp || '';
          itemElement.classList.add('report-list__item--loading');
          itemElement.setAttribute('aria-busy', 'true');
          const spinner = itemElement.querySelector('.loading__spinner');
          if (spinner) {
            spinner.classList.remove('hidden');
          }
        }
        try {
          const response = await fetch('https://gserver1.btbp.org/deeptag/AppService.svc/getTagResults', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              APIKey: 'REEY-K6P8-FP16-QMIW',
              ImageId: Array.isArray(imageId) ? imageId[0] : imageId,
              Tags: DEEPTAG_REQUIRED_TAGS
            })
          });
          if (!response.ok) {
            const isServerError = response.status >= 500;
            throw new Error(isServerError ? 'Server error. Please try again.' : 'getTagResults API failed');
          }
          const getTagsResponse = await response.json();
          this.updateAssessmentState({ getTagsResponse });
          if (typeof window.buildReportFromResponse === 'function') {
            window.buildReportFromResponse(getTagsResponse);
          }
          if (typeof window.updateWeatherDisplay === 'function') {
            window.updateWeatherDisplay(getTagsResponse);
          }
          this.cameFromReportList = true;
          this.showSlide(5, false, true);
        } catch (err) {
          console.error('Error fetching tag results:', err);
          this.showReportItemError(err.message || 'Failed to load report. Please try again.');
        } finally {
          if (itemElement) {
            itemElement.classList.remove('report-list__item--loading');
            itemElement.removeAttribute('aria-busy');
            const spinner = itemElement.querySelector('.loading__spinner');
            if (spinner) {
              spinner.classList.add('hidden');
            }
          }
        }
      }

      initReportBackButton() {
        const reportBackBtn = this.querySelector('[data-action="report-back"]');
        if (reportBackBtn) {
          reportBackBtn.addEventListener('click', () => {
            this.showSlide(1, true);
          });
        }
      }

      renderSlide5CustomerInfo() {
        const wrap = this.querySelector('#skin-report__customer-info');
        if (!wrap) return;
        const customer = window.reportsResponse?.data || window.reportsResponse?.customer || window.reportsResponse;
        if (!customer) {
          wrap.style.display = 'none';
          return;
        }
        const nameEl = this.querySelector('#skin-report__customer-name');
        const metaEl = this.querySelector('#skin-report__customer-meta');
        const dateEl = this.querySelector('#skin-report__customer-date');
        if (nameEl) {
          const first = customer.firstName || customer.first_name || '';
          const last = customer.lastName || customer.last_name || '';
          nameEl.textContent = [first, last].filter(Boolean).join(' ') || customer.name || '—';
        }
        const parts = [];
        if (customer.email) parts.push(customer.email);
        if (customer.phone || customer.phoneNumber) parts.push(customer.phone || customer.phoneNumber);
        if (metaEl) metaEl.textContent = parts.join(' · ') || '';
        if (dateEl) {
          const ts = this.currentReportTimestamp || '';
          dateEl.textContent = ts ? this.formatReportTimestamp(ts) : '';
        }
        wrap.style.display = 'block';
      }

      formatReportTimestamp(ts) {
        if (!ts) return '';
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) return ts;
        return d.toLocaleDateString(undefined, { dateStyle: 'medium' });
      }

      hideSlide5CustomerInfo() {
        const wrap = this.querySelector('#skin-report__customer-info');
        if (wrap) wrap.style.display = 'none';
      }

      showReportItemError(message) {
        const container =
          this.querySelector('.report-list__items') ||
          this.querySelector('.report-list-items');
      
        if (!container) return;
      
        const existingBanner = container.querySelector('.report-list__error--banner');
      
        const banner = document.createElement('p');
        banner.className = 'report-list__error report-list__error--banner';
        banner.textContent = message;
      
        if (existingBanner) {
          // Replace existing error only
          existingBanner.replaceWith(banner);
        } else {
          // Add new error at top
          container.prepend(banner);
        }
      
        setTimeout(() => {
          banner.remove();
        }, 5000);
      }      

      // Determine highest valid slide based on strict gated flow
      // Slide selection derived from data only (no currentSlide persistence)
      getHighestValidSlide() {
        const state = this.getAssessmentState();
        const hasCustomerId = state.customerId && state.customerId.trim().length > 0;
        const step1Complete = this.isStep1Complete();
        const step2Complete = this.isStep2Complete();
        const formComplete = this.isFormComplete();
        const locationResolved = this.isLocationDecisionResolved();
        const hasBase64 = state.captureBase64 && state.captureBase64.length > 0;
        const hasGetTags = state.getTagsResponse && typeof state.getTagsResponse === 'object';
        
        // AUTHORITATIVE RULE 1: Slide 1 (Entry)
        // If phone/email missing → Step 1
        if (!step1Complete) {
          return 1;
        }
        
        // AUTHORITATIVE RULE 2: Slide 2 (Basic Information)
        // If basic info missing (name) → Step 2
        if (!step2Complete) {
          return 2;
        }
        
        // At this point, Step 1 and Step 2 are complete
        // Check if customer needs to be created or location needs to be resolved
        
        // If customerId doesn't exist, we need to create it (but can't proceed to Slide 3 yet)
        // This will be handled in recovery logic
        if (!hasCustomerId) {
          // Forms complete but customer not created - still on Slide 2
          // (Customer will be created before location resolution)
          return 2;
        }
        
        // AUTHORITATIVE RULE: Slide 3 (Camera / Scan)
        // User may reach Slide 3 ONLY if:
        // - customerId exists
        // - location decision is resolved (allowed or skipped)
        if (hasCustomerId && !locationResolved) {
          // Customer exists but location not resolved - still on Slide 2
          return 2;
        }
        
        // If we reach here, customerId exists, form is complete, and location is resolved
        // Now check for assessment data (slides 4-5)
        
        // Slide 5: Requires both base64 AND getTags
        if (hasBase64 && hasGetTags) {
          return 5;
        }
        
        // Slide 4: Requires base64
        if (hasBase64) {
          return 4;
        }
        
        // Slide 3: All prerequisites met (customerId, form complete, location resolved)
        return 3;
      }

      // Validate if a slide can be accessed based on strict gated flow
      canAccessSlide(slideNumber) {
        const state = this.getAssessmentState();
        const hasCustomerId = state.customerId && state.customerId.trim().length > 0;
        const step1Complete = this.isStep1Complete();
        const step2Complete = this.isStep2Complete();
        const formComplete = this.isFormComplete();
        const locationResolved = this.isLocationDecisionResolved();
        const hasBase64 = state.captureBase64 && state.captureBase64.length > 0;
        const hasGetTags = state.getTagsResponse && typeof state.getTagsResponse === 'object';
        
        // AUTHORITATIVE RULE 1: Slide 1 (Entry)
        // If phone/email missing → Step 1
        if (slideNumber === 1) {
          return !step1Complete;
        }
        
        // AUTHORITATIVE RULE 2: Slide 2 (Basic Information)
        // If basic info missing (name) → Step 2
        if (slideNumber === 2) {
          // Slide 2 is allowed if Step 1 is complete
          return step1Complete;
        }
        
        // AUTHORITATIVE RULE: Slide 3 (Camera / Scan)
        // User may reach Slide 3 ONLY if:
        // - customerId exists
        // - location decision is resolved (allowed or skipped)
        if (slideNumber === 3) {
          return hasCustomerId && formComplete && locationResolved;
        }
        
        // Slide 4: Requires base64 AND all Slide 3 prerequisites
        if (slideNumber === 4) {
          return hasCustomerId && formComplete && locationResolved && hasBase64;
        }
        
        // Slide 5: Requires both base64 AND getTags AND all Slide 3 prerequisites
        if (slideNumber === 5) {
          return hasCustomerId && formComplete && locationResolved && hasBase64 && hasGetTags;
        }
        
        // Slide 8 (report list): only reachable via URL customerId flow (forceShow), not normal navigation
        if (slideNumber === 8) {
          return false;
        }

        // All other slides (6, 7): allowed if customerId exists and form complete
        return hasCustomerId && formComplete;
      }
 
      loadFormDataIntoForms() {
        if (!this.formData) return;
 
        // Load contact form data (slide 1)
        if (this.contactForm) {
          if (this.formData.whatsappNumber) {
            const whatsappInput = this.contactForm.querySelector('#whatsapp-number');
            if (whatsappInput) whatsappInput.value = this.formData.whatsappNumber;
          }
 
          if (this.formData.email) {
            const emailInput = this.contactForm.querySelector('#email-address');
            if (emailInput) emailInput.value = this.formData.email;
          }
        }
 
        // Load main form data (slide 2)
        if (this.form) {
          if (this.formData.name) {
            const nameInput = this.form.querySelector('#user-name');
            if (nameInput) nameInput.value = this.formData.name;
          }
 
          if (this.formData.dateOfBirth) {
            const dobInput = this.form.querySelector('#date-of-birth');
            const dayInput = this.form.querySelector('#date-day');
            const monthInput = this.form.querySelector('#date-month');
            const yearInput = this.form.querySelector('#date-year');
            
            if (dobInput) {
              dobInput.value = this.formData.dateOfBirth;
              
              // Populate the three separate fields if they exist
              if (dayInput && monthInput && yearInput) {
                // Parse date format (YYYY-MM-DD or DD/MM/YYYY)
                const parts = this.formData.dateOfBirth.split(/[-/]/);
                if (parts.length === 3) {
                  // Check if first part is year (4 digits) or day (1-2 digits)
                  if (parts[0].length === 4) {
                    // Format: YYYY-MM-DD
                    yearInput.value = parts[0];
                    monthInput.value = parts[1].replace(/^0+/, '') || parts[1];
                    dayInput.value = parts[2].replace(/^0+/, '') || parts[2];
                  } else {
                    // Format: DD/MM/YYYY
                    dayInput.value = parts[0].replace(/^0+/, '') || parts[0];
                    monthInput.value = parts[1].replace(/^0+/, '') || parts[1];
                    yearInput.value = parts[2];
                  }
                }
              }
            }
          }
 
          if (this.formData.allergies) {
            const allergiesInput = this.form.querySelector(`input[name="allergies"][value="${this.formData.allergies}"]`);
            if (allergiesInput) allergiesInput.checked = true;
          }
        }
      }
 
      handleCompletion() {
        // Update state with completion data
        this.updateAssessmentState({
          formData: {
          ...this.formData,
          completed: true,
          completedAt: new Date().toISOString(),
          }
        });
 
        // Dispatch custom event for other scripts to listen to
        this.dispatchEvent(
          new CustomEvent('skin-assessment-complete', {
            detail: this.getAssessmentState(),
            bubbles: true,
          })
        );

        // You can add navigation or other completion logic here
      }
 
      // Method to get all collected data
      getFormData() {
        return { ...this.formData };
      }
 
      // Deprecated: Use updateAssessmentState({ currentSlide }) instead
      // Kept for backward compatibility
      saveCurrentSlide(slideNumber) {
        this.updateAssessmentState({ currentSlide: slideNumber });
      }

      // Deprecated: Use getAssessmentState().currentSlide instead
      // Kept for backward compatibility
      loadCurrentSlide() {
        const state = this.getAssessmentState();
        return state.currentSlide;
      }

      // Method to reset the assessment
      reset() {
        // Clear all state
        this.clearAssessmentState();
        
        // Sync component properties
        this.formData = {};
        this.currentSlide = 1;
        
        // Reset forms
        if (this.contactForm) {
          this.contactForm.reset();
        }
        if (this.form) {
          this.form.reset();
        }
        
        this.showSlide(1);
      }
    }
  );
}
 